/* ======================================================================
   The Lentil Hub - Custom Bundle Builder
   Premium bundle builder with 20% discount
   ====================================================================== */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    collectionHandle: 'pre-seasoned-lentils',
    discountType: 'tiered', // Changed to tiered pricing
    subscriptionDiscountPercent: 10, // 10% off for subscriptions
    // Which selling plan group a bundle subscription should use. Named rather than
    // positional: a product can carry more than one group (a second app's plan, or a
    // new plan mid-migration), and picking by array position silently decides which
    // plan — and which price — the customer is signed up to.
    subscriptionPlanGroupName: 'TLH Subscribe and Save 10%',
    freeShippingThreshold: 65,
    bundleProductHandle: 'build-your-own-bundle', // Your bundle product handle

    // Tiered pricing: Get price per jar based on quantity
    getTieredPrice: (quantity, isSubscription) => {
      if (isSubscription) {
        // Subscription: Flat 10% off ($11.70/jar)
        return 1170; // $11.70 in cents
      }

      // One-time tiered pricing
      if (quantity <= 4) {
        return 1300; // $13/jar
      } else if (quantity === 5) {
        return 1300; // $13/jar (free shipping kicks in at $65)
      } else {
        return 1200; // $12/jar for 6+
      }
    },
  };

  // Spice level data
  const SPICE_LEVELS = {
    'Sunny Toor Mix': { level: 'Medium', color: '#f59e0b' },
    'Meadow Mix': { level: 'Medium / Hot', color: '#dc2626' },
    'Trinity Mix': { level: 'Medium', color: '#f59e0b' },
    'Mung Mix': { level: 'Mild', color: '#16a34a' },
    'Sundown Mix': { level: 'Medium', color: '#f59e0b' },
    'Yogi Mix': { level: 'Mild', color: '#16a34a' },
    'Vida Mix': { level: 'Mild / Medium', color: '#16a34a' }
  };

  // Ingredients data (per product)
  const PRODUCT_INGREDIENTS = {
    'Sunny Toor Mix': 'Organic Yellow Toor Lentils, Organic Red Masoor Lentils, Turmeric, Paprika, Spice Mix (Garlic, Onion, Coriander, Cumin, Mango, Fenugreek, Chili), Bay Leaf, Salt',
    'Meadow Mix': 'Organic Brown Masoor Lentils, Organic Green Split Peas, Turmeric, Chili Powder, Onion, Spice Mix (Garlic, Coriander, Ginger, Mango Powder), Salt',
    'Trinity Mix': 'Organic Black Urad Lentils, Organic Green Mung Lentils, Organic Brown Lentils, Turmeric, Paprika, Spice mix (Black pepper, Garlic, Onion, Cumin, Mango powder, Cinnamon, Garam Masala, Chili), Salt',
    'Mung Mix': 'Organic Yellow Mung Lentils, Turmeric, Paprika, Spice Mix (Garlic, Toasted Onion, Coconut, Fenugreek, Cumin), Salt. Contains Coconut.',
    'Sundown Mix': 'Organic Steel Cut Oats, Organic Yellow Mung Lentils, Organic Green Split Peas, Peanuts, Chives, Spice Mix (Chili, Coriander, Mango powder, Garlic powder, Black pepper), Salt. Contains Peanuts.',
    'Yogi Mix': 'Organic Red Masoor Lentils, Organic Gluten-Free Rolled Oats, Organic White Rice, Chives, Onion, Spice Mix (Cumin, Garlic, Dried Mango, Chilli, Turmeric), Salt.',
    'Vida Mix': 'Organic Red Quinoa, Organic Mung Lentils, Organic Black Urad Lentils, Turmeric, Spice Mix (Cumin, Onion, Coriander, Black Pepper, Mango powder, Mustard and Ginger Powders, Mint leaves/Chives), Salt.',
  };

  // Bundle state
  const state = {
    products: [],
    bundle: new Map(), // productId -> { product, quantity }
    loading: false,
    bundleProduct: null,
    isSubscription: false, // Default to one-time purchase
    isSaltFree: false, // Track salt-free status
    presetKey: null, // Track which preset was selected ('variety' or 'family')
  };

  // Utility functions
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  // Resolve which selling plan a bundle subscription should use.
  //
  // Match the group by name instead of taking selling_plan_groups[0]. The .js product
  // endpoint returns every group attached to the product regardless of which app owns
  // it, so as soon as a second group exists the first entry is arbitrary — a bundle
  // customer could end up on a different plan, at a different price, than the product
  // page sells. Falls back to the first group if the named one is missing, so a rename
  // degrades to the old behaviour rather than silently dropping the subscription.
  const resolveSellingPlanId = (product) => {
    const groups = product.selling_plan_groups || [];
    if (!groups.length) return null;

    const named = groups.find(g => g.name === CONFIG.subscriptionPlanGroupName);
    const group = named || groups[0];
    if (!named) {
      console.warn(
        `[bundle-builder] selling plan group "${CONFIG.subscriptionPlanGroupName}" not found on ` +
        `${product.handle || 'product'} — falling back to "${group.name}". ` +
        `Check CONFIG.subscriptionPlanGroupName if the plan was renamed or replaced.`
      );
    }
    return group.selling_plans?.[0]?.id ?? null;
  };

  const formatMoney = (cents) => {
    const amount = cents / 100;
    // The frame prints the currency after every figure — "$13.00 USD".
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount) + ' USD';
  };

  // Fetch products from collection
  async function fetchProducts() {
    try {
      const response = await fetch(`/collections/${CONFIG.collectionHandle}/products.json?limit=250`);
      const data = await response.json();
      const products = data.products || [];

      // Fetch full product data (including selling plans) for each product
      const productsWithSellingPlans = await Promise.all(
        products.map(async (product) => {
          try {
            // Fetch from .js endpoint to get selling plans and full product data
            // .js endpoint returns prices in cents and includes selling_plan_groups
            const productResponse = await fetch(`/products/${product.handle}.js`);
            const fullProduct = await productResponse.json();

            // Normalize: .js endpoint uses 'description' but code expects 'body_html'
            if (!fullProduct.body_html && fullProduct.description) {
              fullProduct.body_html = fullProduct.description;
            }

            return fullProduct;
          } catch (error) {
            console.warn(`Failed to fetch full product data for ${product.handle}:`, error);
            return product; // Return basic product if fetch fails
          }
        })
      );

      // Only show products that have at least one available variant
      return productsWithSellingPlans.filter(p =>
        p.variants && p.variants.some(v => v.available)
      );
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  }

  // Fetch bundle product (the container product for checkout)
  async function fetchBundleProduct() {
    try {
      const response = await fetch(`/products/${CONFIG.bundleProductHandle}.js`);
      const product = await response.json();
      return product;
    } catch (error) {
      console.error('Error fetching bundle product:', error);
      return null;
    }
  }

  // Calculate bundle totals
  function calculateTotals() {
    let itemCount = 0;

    // Count total items
    state.bundle.forEach(({ quantity }) => {
      itemCount += quantity;
    });

    // Get price per jar based on tier
    const pricePerJar = CONFIG.getTieredPrice(itemCount, state.isSubscription);
    const subtotal = pricePerJar * itemCount;

    // Free shipping eligibility
    const qualifiesForFreeShipping =
      subtotal >= (CONFIG.freeShippingThreshold * 100) || // $65+
      (itemCount >= 5 && !state.isSubscription); // 5+ jars one-time

    // Calculate savings (for display purposes)
    let savings = 0;
    if (state.isSubscription) {
      // Show subscription savings (10% off)
      savings = itemCount * 1300 * 0.1; // 10% of regular price
    } else if (itemCount >= 6) {
      // Show tiered pricing savings ($1 off per jar)
      savings = itemCount * 100; // $1 per jar in cents
    }

    return {
      subtotal,
      total: subtotal,
      itemCount,
      pricePerJar,
      qualifiesForFreeShipping,
      savings,
    };
  }

  // Add product to bundle
  function addToBundle(product) {
    const existing = state.bundle.get(product.id);
    if (existing) {
      existing.quantity += 1;
    } else {
      state.bundle.set(product.id, { product, quantity: 1 });
    }
    render();
    dispatchBundleUpdate();
  }

  // Remove product from bundle
  function removeFromBundle(productId) {
    const existing = state.bundle.get(productId);
    if (existing) {
      if (existing.quantity > 1) {
        existing.quantity -= 1;
      } else {
        state.bundle.delete(productId);
      }
    }
    render();
    dispatchBundleUpdate();
  }

  // Clear entire bundle
  function clearBundle() {
    state.bundle.clear();
    state.presetKey = null; // Clear preset selection when bundle is cleared
    render();
    dispatchBundleUpdate();
  }

  // Add specific quantity
  function setQuantity(productId, quantity) {
    const existing = state.bundle.get(productId);
    if (existing && quantity > 0) {
      existing.quantity = quantity;
      render();
      dispatchBundleUpdate();
    } else if (quantity === 0) {
      state.bundle.delete(productId);
      render();
      dispatchBundleUpdate();
    }
  }

  // Dispatch custom event when bundle updates
  function dispatchBundleUpdate() {
    const totals = calculateTotals();
    document.dispatchEvent(new CustomEvent('tlh:bundle:updated', {
      detail: { bundle: state.bundle, totals }
    }));
  }

  // Add bundle to Shopify cart
  async function addBundleToCart() {
    if (state.bundle.size === 0) {
      showToast('Please add at least one item to your bundle');
      return;
    }

    // Generate unique bundle ID using timestamp
    const bundleId = `bundle_${Date.now()}`;
    const totals = calculateTotals();

    // Prepare items array - individual products for inventory tracking
    const items = [];

    // Add each product with bundle properties
    state.bundle.forEach(({ product, quantity }) => {
      const item = {
        id: product.variants[0].id,
        quantity: quantity,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_price': totals.pricePerJar, // Display price per jar
          '_bundle_total': totals.total, // Total bundle price
          '_is_subscription': state.isSubscription ? 'yes' : 'no',
        }
      };

      // Add salt-free property if enabled
      if (state.isSaltFree) {
        item.properties['_salt_free'] = 'yes';
      }

      // Add bundle preset type if selected (variety sampler or family favorites)
      if (state.presetKey) {
        item.properties['_bundle_type'] = state.presetKey === 'variety' ? 'Variety Sampler' :
                                          state.presetKey === 'family' ? 'Family Favorites' :
                                          state.presetKey;
      }

      // Add selling plan for subscription
      if (state.isSubscription) {
        const planId = resolveSellingPlanId(product);
        if (planId) item.selling_plan = planId;
      }

      items.push(item);
    });

    const formData = { items };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        showToast('Bundle added to cart! 🎉');

        // Trigger cart drawer/update if your theme has it
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Redirect to cart (automatic discount will apply at checkout)
        setTimeout(() => {
          window.location.href = '/cart';
        }, 1000);
      } else {
        const error = await response.json();
        showToast(`Error: ${error.description || 'Could not add to cart'}`);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast('Error adding to cart. Please try again.');
    }
  }

  // Show toast notification
  function showToast(message) {
    const toast = document.createElement('div');
    toast.className = 'tlh-toast show';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // Get product label (Best Seller, Trending Now)
  function getProductLabel(title) {
    // `variant` picks the pill colour in CSS — the frame gives each label its
    // own: orange for best seller, salmon for trending. Sundown Mix's gold
    // "new" pill was dropped at the owner's request; its CSS variant is left
    // in place in case it comes back.
    const labels = {
      'Sunny Toor Mix': { text: 'BEST SELLER', variant: 'best' },
      'Vida Mix': { text: 'TRENDING', variant: 'trending' }
    };
    return labels[title] || null;
  }

  // Render product card
  function renderProductCard(product) {
    const spiceInfo = SPICE_LEVELS[product.title] || null;
    const variant = product.variants && product.variants[0];
    const price = variant ? variant.price : 0; // Already in cents from /products/{handle}.js
    const inBundle = state.bundle.has(product.id);
    const quantity = inBundle ? state.bundle.get(product.id).quantity : 0;
    const label = getProductLabel(product.title);

    // Parse product description to get flavor profile from product data
    const descriptionData = parseProductDescription(product.body_html || '');
    let flavorDesc = descriptionData.flavorProfile || getFlavorProfileForProduct(product.title);

    // Strip HTML tags for card display (keep plain text only)
    if (flavorDesc && flavorDesc.includes('<')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = flavorDesc;
      flavorDesc = tempDiv.textContent || tempDiv.innerText || flavorDesc;
    }

    // The card shows this as a label rather than a sentence: trailing full stop
    // off, every word capitalised EXCEPT the joining words, which stay lower
    // case ("Tangy and Flavorful", not "Tangy And Flavorful"). CSS
    // `text-transform: capitalize` cannot make that exception, which is why it
    // was dropped from .tlh-product-flavor in favour of doing it here. Done at
    // render, not in the copy, because the text can arrive either from the
    // product description or the fallback map below.
    if (flavorDesc) {
      flavorDesc = flavorDesc.trim().replace(/\.+$/, '').replace(
        /[A-Za-z]+/g,
        function (word, offset) {
          var lower = word.toLowerCase();
          if (offset > 0 && FLAVOR_MINOR_WORDS.indexOf(lower) !== -1) return lower;
          return lower.charAt(0).toUpperCase() + lower.slice(1);
        }
      );
    }

    // Get image - try multiple properties
    let imageUrl = '';
    if (product.featured_image) {
      imageUrl = product.featured_image;
    } else if (product.image?.src) {
      imageUrl = product.image.src;
    } else if (product.images && product.images[0]) {
      // Handle case where images[0] might be a string URL or an object with src property
      imageUrl = typeof product.images[0] === 'string'
        ? product.images[0]
        : product.images[0]?.src || '';
    }

    return `
      <div class="tlh-product-card ${inBundle ? 'in-bundle' : ''}" data-product-id="${product.id}" data-shopify-track="false">
        ${label ? `<div class="tlh-product-label tlh-product-label--${label.variant}">${label.text}</div>` : ''}
        <div class="tlh-product-image" data-action="view-details" data-product-id="${product.id}" style="cursor: pointer;" data-shopify-track="false">
          <img src="${imageUrl}" alt="${product.title}" loading="lazy">
          <!-- The badge sits on the oval's bottom-right edge in Figma, so it has to
               be positioned against the oval rather than the whole card. -->
          ${quantity > 0 ? `<div class="tlh-product-badge">${quantity}</div>` : ''}
        </div>
        <div class="tlh-product-info" data-action="view-details" data-product-id="${product.id}" style="cursor: pointer;" data-shopify-track="false">
          <h3 class="tlh-product-title">${product.title}</h3>
          <p class="tlh-product-flavor">${flavorDesc}</p>
          <div class="tlh-product-price">${formatMoney(price)}</div>
        </div>
        <button
          class="tlh-product-add-btn"
          data-action="add"
          data-product-id="${product.id}"
          data-shopify-track="false"
          aria-label="Add ${product.title} to bundle"
        >${inBundle ? 'Add Another' : 'Add'}</button>
      </div>
    `;
  }

  // Render bundle item in footer
  function renderBundleItem(productId, { product, quantity }) {
    const variant = product.variants && product.variants[0];
    const price = variant ? variant.price : 0; // Already in cents from /products/{handle}.js

    // Get image - try multiple properties
    let imageUrl = '';
    if (product.featured_image) {
      imageUrl = product.featured_image;
    } else if (product.image?.src) {
      imageUrl = product.image.src;
    } else if (product.images && product.images[0]) {
      // Handle case where images[0] might be a string URL or an object with src property
      imageUrl = typeof product.images[0] === 'string'
        ? product.images[0]
        : product.images[0]?.src || '';
    }

    return `
      <div class="tlh-bundle-item" data-product-id="${productId}">
        <img src="${imageUrl}" alt="${product.title}" class="tlh-bundle-item-img">
        <div class="tlh-bundle-item-info">
          <div class="tlh-bundle-item-title">${product.title}${state.isSaltFree ? ' <span class="tlh-salt-tag">NO ADDED SALT</span>' : ''}</div>
          <div class="tlh-bundle-item-price">${formatMoney(price)}</div>
        </div>
        <div class="tlh-bundle-item-controls">
          <button class="tlh-qty-btn" data-action="decrease" data-product-id="${productId}" aria-label="Decrease quantity">−</button>
          <span class="tlh-qty-display">${quantity}</span>
          <button class="tlh-qty-btn" data-action="increase" data-product-id="${productId}" aria-label="Increase quantity">+</button>
        </div>
        <button class="tlh-bundle-item-remove" data-action="remove" data-product-id="${productId}" aria-label="Remove from bundle">Remove</button>
      </div>
    `;
  }

  // Render entire bundle builder
  function render() {
    const container = $('#tlh-bundle-builder');
    if (!container) return;

    const productsGrid = $('.tlh-products-grid', container);
    const bundleFooter = $('.tlh-bundle-footer', container);

    // Render products
    if (productsGrid) {
      productsGrid.innerHTML = state.products.map(renderProductCard).join('');
    }

    // Render bundle footer
    if (bundleFooter) {
      const totals = calculateTotals();
      const bundleItems = $('.tlh-bundle-items', bundleFooter);
      const emptyState = $('.tlh-bundle-empty', bundleFooter);
      const collapsedBar = $('.tlh-bundle-collapsed-bar', bundleFooter);

      const yourBundleHeader = $('.tlh-your-bundle-header', bundleFooter);
      const bundleLeft = $('.tlh-bundle-left', bundleFooter);

      if (state.bundle.size === 0) {
        emptyState.style.display = 'block';
        bundleItems.style.display = 'none';
        $('.tlh-bundle-summary', bundleFooter).style.display = 'none';
        if (yourBundleHeader) yourBundleHeader.style.display = 'none';
        if (bundleLeft) bundleLeft.style.display = 'none';
        if (collapsedBar) collapsedBar.style.display = 'none';
        bundleFooter.classList.remove('expanded');
      } else {
        emptyState.style.display = 'none';
        bundleItems.style.display = 'block';
        // flex, not block: the summary is a flex column so the phone layout can
        // pull the item-count row above the plan pills the way the frame draws it.
        $('.tlh-bundle-summary', bundleFooter).style.display = 'flex';
        if (yourBundleHeader) yourBundleHeader.style.display = 'block';
        if (bundleLeft) bundleLeft.style.display = 'block';

        // Show collapsed bar on mobile when items exist
        if (collapsedBar) {
          collapsedBar.style.display = '';
          // Update collapsed bar values
          $('.tlh-collapsed-items', collapsedBar).textContent = `${totals.itemCount} ${totals.itemCount === 1 ? 'item' : 'items'}`;
          $('.tlh-collapsed-total', collapsedBar).textContent = formatMoney(totals.total);

          // Update purchase type indicator
          const purchaseTypeEl = $('.tlh-collapsed-purchase-type', collapsedBar);
          if (purchaseTypeEl) {
            if (state.isSubscription) {
              purchaseTypeEl.textContent = 'Subscription • Save 10%';
              purchaseTypeEl.style.color = '#059669';
            } else {
              purchaseTypeEl.textContent = 'One-time purchase';
              purchaseTypeEl.style.color = '#666';
            }
          }
        }

        // Render bundle items
        bundleItems.innerHTML = Array.from(state.bundle.entries())
          .map(([id, data]) => renderBundleItem(id, data))
          .join('');

        // Update totals
        const subtotalEl = $('.tlh-subtotal-amount', bundleFooter);
        const discountEl = $('.tlh-discount-amount', bundleFooter);
        const totalEl = $('.tlh-total-amount', bundleFooter);
        const itemCountEl = $('.tlh-item-count', bundleFooter);

        // Update subtotal with crossed-out original price if there's a discount
        if (subtotalEl) {
          if (totals.savings > 0) {
            const originalSubtotal = totals.subtotal + totals.savings;
            subtotalEl.innerHTML = `<span style="text-decoration: line-through; color: #999; margin-right: 8px;">${formatMoney(originalSubtotal)}</span>${formatMoney(totals.subtotal)}`;
          } else {
            subtotalEl.textContent = formatMoney(totals.subtotal);
          }
        }
        if (totalEl) totalEl.textContent = formatMoney(totals.total);
        if (itemCountEl) {
          itemCountEl.innerHTML = `<span class="tlh-item-num">${totals.itemCount}</span> ${totals.itemCount === 1 ? 'Item' : 'Items'}`;
        }

        // Free shipping line — plain right-aligned copy in the frame.
        const shippingEl = $('#tlh-free-shipping-tracker', bundleFooter);
        if (shippingEl) {
          if (totals.qualifiesForFreeShipping) {
            shippingEl.textContent = 'You Are Eligible For Free Shipping!';
          } else {
            const remaining = (CONFIG.freeShippingThreshold * 100) - totals.subtotal;
            shippingEl.textContent = `Add ${formatMoney(remaining)} more for FREE shipping`;
          }
        }

        // Show/hide discount row based on savings
        const discountRow = bundleFooter.querySelector('.tlh-price-row.discount');
        if (totals.savings > 0) {
          if (discountRow) discountRow.style.display = 'flex';
          if (discountEl) {
            discountEl.textContent = `-${formatMoney(totals.savings)}`;
            // Create descriptive label
            let label;
            if (state.isSubscription) {
              label = `Subscription (10% off)`;
            } else {
              const perJarSavings = totals.savings / totals.itemCount / 100;
              label = `Save $${perJarSavings.toFixed(2)}/jar (${totals.itemCount} items)`;
            }
            const labelEl = discountRow?.querySelector('span:first-child');
            if (labelEl) labelEl.textContent = label;
          }
        } else {
          if (discountRow) discountRow.style.display = 'none';
        }

        // Update subscription savings calculator
        const subSavingsAmount = $('.tlh-sub-savings-amount', bundleFooter);
        const subPricePerJar = $('.tlh-sub-price-per-jar', bundleFooter);

        if (subSavingsAmount && subPricePerJar && totals.itemCount > 0) {
          // Subscription is always 10% off the base $13 price, regardless of quantity tiers
          const basePrice = 1300; // $13/jar base price
          const subscriptionPrice = 1170; // $11.70/jar (10% off)

          const regularSubtotal = basePrice * totals.itemCount;
          const subscriptionSubtotal = subscriptionPrice * totals.itemCount;

          // Calculate savings: difference between base price and subscription price
          const oneTimeSavings = (regularSubtotal - subscriptionSubtotal) / 100; // Convert cents to dollars
          const pricePerJar = subscriptionPrice / 100; // $11.70

          // Update displays
          subSavingsAmount.textContent = oneTimeSavings.toFixed(2);
          subPricePerJar.textContent = pricePerJar.toFixed(2);
        }
      }
    }

    // Sync purchase type radio buttons with state
    syncPurchaseTypeRadios();
  }

  // Sync purchase type radio buttons with state
  function syncPurchaseTypeRadios() {
    document.querySelectorAll('.tlh-purchase-option').forEach(option => {
      const radio = option.querySelector('input[type="radio"]');
      const purchaseType = option.dataset.purchaseType;
      const isActive = (state.isSubscription && purchaseType === 'subscription') ||
                      (!state.isSubscription && purchaseType === 'onetime');

      // Styling is entirely CSS-driven (the Figma pills are cream with an
      // olive border in both states) — setting it inline here used to paint
      // the idle pill white with a #ddd border.
      option.classList.toggle('active', isActive);
      if (radio) radio.checked = isActive;
    });
  }

  // Show product details modal
  function showProductModal(product) {
    const modal = $('#tlh-product-modal');
    if (!modal) return;

    const spiceInfo = SPICE_LEVELS[product.title] || null;
    const variant = product.variants && product.variants[0];
    const price = variant ? variant.price : 0; // Already in cents from /products/{handle}.js

    // Get all product images
    let images = [];
    if (product.images && product.images.length > 0) {
      images = product.images.map(img =>
        typeof img === 'string' ? img : (img?.src || '')
      ).filter(url => url);
    } else if (product.featured_image) {
      images = [product.featured_image];
    } else if (product.image?.src) {
      images = [product.image.src];
    }

    const mainImage = images[0] || '';

    // Parse product description to extract structured information
    const descriptionData = parseProductDescription(product.body_html || '');

    // Build image gallery HTML
    const imageGalleryHtml = images.length > 0 ? `
      <div class="tlh-modal-image-gallery">
        <div class="tlh-modal-main-image">
          <img id="modal-main-img" src="${mainImage}" alt="${product.title}">
        </div>
        ${images.length > 1 ? `
          <div class="tlh-modal-thumbnails">
            ${images.map((img, index) => `
              <div class="tlh-modal-thumb ${index === 0 ? 'active' : ''}" data-image-url="${img}">
                <img src="${img}" alt="${product.title} - Image ${index + 1}">
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    ` : '';

    const modalContent = `
      <div class="tlh-modal-header">
        <h2 class="tlh-modal-title">
          ${product.title}
          ${spiceInfo ? `<span class="spice-pill" style="background-color: ${spiceInfo.color}">${spiceInfo.level}</span>` : ''}
        </h2>
        <button class="tlh-modal-close" data-action="close-modal" aria-label="Close">&times;</button>
      </div>
      <div class="tlh-modal-body">
        <div class="tlh-modal-left">
          ${imageGalleryHtml}
        </div>
        <div class="tlh-modal-right">
          <div class="tlh-modal-price-section">
            <div class="tlh-modal-price">${formatMoney(price)}</div>
            <div class="tlh-modal-meta">
              <span>🥣 <strong>${getPackSize(product.title)}</strong></span> ·
              <span>🍴 <strong>4–5 Meals</strong></span>
            </div>
          </div>

          <div class="feature-badge-row">
            <span class="feature-badge" data-icon="vegan">
              <span class="feature-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8S2 12 2 12Z"/><path d="M12 4v16"/></svg></span>
              <span class="feature-label">Vegan</span>
            </span>
            <span class="feature-badge" data-icon="gluten-free">
              <span class="feature-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20L20 4"/><path d="M7 17s-1-6 5-13c6 7 5 13 5 13"/></svg></span>
              <span class="feature-label">Gluten-free</span>
            </span>
            ${product.title === 'Sundown Mix' ? `
            <span class="feature-badge" data-icon="contains-peanuts">
              <span class="feature-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><ellipse cx="12" cy="13" rx="7" ry="8"/><path d="M9 5c1.5-2 4.5-2 6 0"/><path d="M12 10v4"/><circle cx="12" cy="17" r="0.5" fill="currentColor"/></svg></span>
              <span class="feature-label">Contains Peanuts</span>
            </span>
            ` : `
            <span class="feature-badge" data-icon="nut-free">
              <span class="feature-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><ellipse cx="12" cy="13" rx="7" ry="8"/><path d="M9 5c1.5-2 4.5-2 6 0"/><path d="M4 4l16 16"/></svg></span>
              <span class="feature-label">Nut-free</span>
            </span>
            `}
            <span class="feature-badge" data-icon="gmo-free">
              <span class="feature-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M6 6l12 12"/></svg></span>
              <span class="feature-label">GMO-Free</span>
            </span>
          </div>

          ${descriptionData.whatsInside ? `
            <div class="tlh-modal-section">
              <h3 class="tlh-modal-section-title">What's Inside</h3>
              <div class="tlh-modal-section-content">${descriptionData.whatsInside}</div>
            </div>
          ` : ''}

          ${descriptionData.whatItMakes ? `
            <div class="tlh-modal-section">
              <h3 class="tlh-modal-section-title">What it Makes</h3>
              <div class="tlh-modal-section-content">${descriptionData.whatItMakes}</div>
            </div>
          ` : ''}

          ${(PRODUCT_INGREDIENTS[product.title] || descriptionData.ingredients) ? `
            <div class="tlh-modal-section">
              <h3 class="tlh-modal-section-title">Ingredients</h3>
              <div class="tlh-modal-section-content">${PRODUCT_INGREDIENTS[product.title] || descriptionData.ingredients}</div>
            </div>
          ` : ''}

          ${spiceInfo ? `
            <div class="tlh-modal-section">
              <h3 class="tlh-modal-section-title">Heat Level</h3>
              <div class="tlh-modal-section-content">
                <span class="spice-pill" style="background-color: ${spiceInfo.color}; padding: 4px 12px; border-radius: 6px; color: white; font-weight: 600;">${spiceInfo.level}</span>
                <span style="margin-left: 8px;">${getHeatLevelDescription(spiceInfo.level)}</span>
              </div>
            </div>
          ` : ''}

          ${descriptionData.flavorProfile || spiceInfo ? `
            <div class="tlh-modal-section">
              <h3 class="tlh-modal-section-title">Flavor Profile</h3>
              <div class="tlh-modal-section-content">
                ${descriptionData.flavorProfile || getFlavorProfileForProduct(product.title)}
              </div>
            </div>
          ` : ''}

          ${descriptionData.nutritionalHighlights ? `
            <div class="tlh-modal-section">
              <h3 class="tlh-modal-section-title">Nutritional Highlights</h3>
              <div class="tlh-modal-section-content">${descriptionData.nutritionalHighlights}</div>
            </div>
          ` : ''}

          ${descriptionData.howToCook ? `
            <div class="tlh-modal-section">
              <h3 class="tlh-modal-section-title">How to Cook</h3>
              <div class="tlh-modal-section-content">${descriptionData.howToCook}</div>
            </div>
          ` : ''}

          ${descriptionData.servingSuggestions ? `
            <div class="tlh-modal-section">
              <h3 class="tlh-modal-section-title">Serving Suggestions</h3>
              <div class="tlh-modal-section-content">${descriptionData.servingSuggestions}</div>
            </div>
          ` : ''}

          <button class="tlh-product-add-btn tlh-modal-add-btn" data-action="add-from-modal" data-product-id="${product.id}" data-shopify-track="false">
            Add to Bundle
          </button>
        </div>
      </div>
    `;

    $('.tlh-modal-content', modal).innerHTML = modalContent;
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Add thumbnail click handlers
    setupImageGallery(modal);
  }

  // Setup image gallery interactions
  function setupImageGallery(modal) {
    const thumbnails = $$('.tlh-modal-thumb', modal);
    const mainImg = $('#modal-main-img', modal);

    if (!mainImg) return;

    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', function() {
        // Update active state
        thumbnails.forEach(t => t.classList.remove('active'));
        this.classList.add('active');

        // Update main image
        const newImageUrl = this.dataset.imageUrl;
        mainImg.src = newImageUrl;
      });
    });
  }

  // Parse product description HTML to extract structured data
  function parseProductDescription(html) {
    const data = {
      whatsInside: '',
      whatItMakes: '',
      flavorProfile: '',
      nutritionalHighlights: '',
      howToCook: '',
      servingSuggestions: '',
      ingredients: ''
    };

    if (!html) return data;

    // Create a temporary div to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Helper function to extract section with preserved HTML
    function extractSection(sectionName, nextSections) {
      // Find the section heading in the HTML
      const htmlContent = temp.innerHTML;
      const regex = new RegExp(`${sectionName}[:\\s]+(.*?)(?=${nextSections}|$)`, 'is');
      const match = htmlContent.match(regex);
      if (match) {
        // Clean up the HTML content
        let content = match[1].trim();

        // Create a temp element to clean up the HTML
        const cleanTemp = document.createElement('div');
        cleanTemp.innerHTML = content;

        // Remove empty list items and stray bullets
        const listItems = cleanTemp.querySelectorAll('li');
        listItems.forEach(li => {
          const text = li.textContent.trim();
          // Remove if empty or just contains bullet characters
          if (!text || text === '•' || text === '·' || text.length < 2) {
            li.remove();
          }
        });

        // Remove empty paragraphs
        const paragraphs = cleanTemp.querySelectorAll('p');
        paragraphs.forEach(p => {
          const text = p.textContent.trim();
          if (!text || text === '•' || text === '·') {
            p.remove();
          }
        });

        // Return cleaned HTML
        return cleanTemp.innerHTML.trim();
      }
      return '';
    }

    // Extract sections with HTML preserved (for links, etc.)
    data.whatsInside = extractSection("What'?s?\\s+[Ii]nside", "What|Flavor|Heat|Nutritional|How|Serving|Ingredients");
    data.whatItMakes = extractSection("What\\s+[Ii]t\\s+[Mm]akes", "What|Flavor|Heat|Nutritional|How|Serving|Ingredients");
    data.flavorProfile = extractSection("Flavor\\s+[Pp]rofile", "What|Heat|Nutritional|How|Serving|Ingredients");
    data.nutritionalHighlights = extractSection("Nutritional\\s+[Hh]ighlights", "What|Flavor|Heat|How|Serving|Ingredients");
    data.howToCook = extractSection("How\\s+to\\s+[Cc]ook", "What|Flavor|Heat|Nutritional|Serving|Ingredients");
    data.servingSuggestions = extractSection("Serving\\s+[Ss]uggestions", "What|Flavor|Heat|Nutritional|How|Ingredients");
    data.ingredients = extractSection("Ingredients", "zzz_never_match");

    return data;
  }

  // Kept lower case inside a flavour label unless they lead it.
  var FLAVOR_MINOR_WORDS = ['and', 'or', 'the', 'a', 'an', 'of', 'with', 'in', 'on', 'for', 'to', 'but', 'nor'];

  // Pack size shown in the card modal. Six of the mixes ship the same pack;
  // Yogi Mix is the one that differs.
  function getPackSize(title) {
    return title === 'Yogi Mix' ? '250g / 8.8oz' : '300g / 10oz';
  }

  // Get default flavor profile based on product title
  function getFlavorProfileForProduct(title) {
    const profiles = {
      'Sunny Toor Mix': 'Tangy and flavorful.',
      'Meadow Mix': 'Rich and aromatic with a spicy kick.',
      'Trinity Mix': 'Earthy and peppery.',
      'Mung Mix': 'Nutty and creamy.',
      'Sundown Mix': 'Warm and comforting.',
      'Yogi Mix': 'Mild and wholesome.',
      'Vida Mix': 'Fresh and vibrant.'
    };
    return profiles[title] || 'Delicious and nutritious.';
  }

  // Get heat level description
  function getHeatLevelDescription(level) {
    const descriptions = {
      'Mild': '— Perfect for those new to spices or who enjoy gentler flavors.',
      'Mild / Medium': '— Gentle warmth with a touch of spice.',
      'Medium': '— Zesty with a balanced warmth.',
      'Medium / Hot': '— Warm and spiced, with a gentle kick.',
      'Hot': '— Bold and fiery!'
    };
    return descriptions[level] || '';
  }

  // Close product details modal
  function closeProductModal() {
    const modal = $('#tlh-product-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = ''; // Restore scrolling
    }
  }

  // Event delegation for all clicks
  function handleClick(e) {
    const target = e.target.closest('[data-action]');
    if (!target) return;

    const action = target.dataset.action;
    const productIdStr = target.dataset.productId;

    // Try both number and string comparison for Shopify product IDs
    const productId = productIdStr ? (isNaN(productIdStr) ? productIdStr : parseInt(productIdStr)) : null;

    console.log('Bundle builder click:', { action, productId, productIdStr });

    switch (action) {
      case 'view-details':
        const productToView = state.products.find(p => p.id == productId);
        if (productToView) {
          showProductModal(productToView);
        }
        break;

      case 'close-modal':
        closeProductModal();
        break;

      case 'add-from-modal':
        const productFromModal = state.products.find(p => p.id == productId);
        if (productFromModal) {
          state.presetKey = null; // Clear preset when manually adding
          addToBundle(productFromModal);
          closeProductModal();
        }
        break;

      case 'add':
        const product = state.products.find(p => p.id == productId); // Use == for flexible comparison
        console.log('Found product:', product);
        if (product) {
          state.presetKey = null; // Clear preset when manually adding
          addToBundle(product);
        } else {
          console.warn('Product not found:', productId, 'Available:', state.products.map(p => p.id));
        }
        break;

      case 'increase':
        const existing = state.bundle.get(productId);
        if (existing) {
          state.presetKey = null; // Clear preset when manually changing quantity
          setQuantity(productId, existing.quantity + 1);
        }
        break;

      case 'decrease':
        state.presetKey = null; // Clear preset when manually removing
        removeFromBundle(productId);
        break;

      case 'remove':
        state.presetKey = null; // Clear preset when manually removing
        state.bundle.delete(productId);
        render();
        dispatchBundleUpdate();
        break;

      case 'clear':
        if (confirm('Clear all items from your bundle?')) {
          clearBundle();
        }
        break;

      case 'toggle-cart':
        const bundleFooter = $('.tlh-bundle-footer');
        if (bundleFooter) {
          bundleFooter.classList.toggle('expanded');
        }
        break;

      case 'add-to-cart':
        addBundleToCart();
        break;

      case 'toggle-subscription':
        state.isSubscription = target.checked;
        render();
        dispatchBundleUpdate();
        break;

      case 'set-purchase-type':
        state.isSubscription = target.value === 'subscription';
        render();
        dispatchBundleUpdate();
        break;

      case 'toggle-saltfree':
        state.isSaltFree = target.checked;
        // Sync all salt-free checkboxes (collapsed and expanded)
        document.querySelectorAll('[data-action="toggle-saltfree"]').forEach(checkbox => {
          checkbox.checked = state.isSaltFree;
        });
        render();
        dispatchBundleUpdate();
        break;
    }
  }

  // Handle quick start bundles
  function handleQuickStart(bundleKey) {
    const bundles = {
      variety: 'all', // Add all products
      family: ['Sunny Toor Mix', 'Meadow Mix', 'Mung Mix', 'Trinity Mix', 'Yogi Mix'], // 5 jars = Free Shipping
      mild: ['Vida Mix', 'Yogi Mix', 'Mung Mix'],
      spicy: ['Meadow Mix', 'Trinity Mix'],
    };

    clearBundle();

    if (bundleKey === 'variety') {
      state.products.forEach(product => addToBundle(product));
    } else {
      const titles = bundles[bundleKey] || [];
      titles.forEach(title => {
        const product = state.products.find(p => p.title === title);
        if (product) addToBundle(product);
      });
    }

    // Store the preset key to tag the order with bundle type
    // Will be cleared if user manually adds/removes items
    state.presetKey = bundleKey;

    // Update QS button visual state
    document.querySelectorAll('.tlh-qs__btn').forEach(btn => {
      const isActive = btn.getAttribute('onclick').includes(`preset:'${bundleKey}'`);
      btn.classList.toggle('tlh-qs__btn--active', isActive);
      const dot = btn.querySelector('.tlh-qs__radio');
      if (dot) dot.classList.toggle('tlh-qs__radio--filled', isActive);
    });
  }

  // Expose API for external scripts
  window.TLHBundleBuilder = {
    addToBundle: (productTitle) => {
      const product = state.products.find(p => p.title === productTitle);
      if (product) addToBundle(product);
    },
    clearBundle,
    loadPreset: handleQuickStart,
    getState: () => ({ ...state, totals: calculateTotals() }),
  };

  // Initialize
  async function init() {
    const container = $('#tlh-bundle-builder');
    if (!container) return;

    // Prevent Shopify from tracking bundle builder interactions
    container.setAttribute('data-shopify-track', 'false');

    state.loading = true;
    container.classList.add('loading');

    // Fetch data
    const [products, bundleProduct] = await Promise.all([
      fetchProducts(),
      fetchBundleProduct(),
    ]);

    state.products = products;
    state.bundleProduct = bundleProduct;
    state.loading = false;
    container.classList.remove('loading');

    if (!bundleProduct) {
      console.warn('Bundle product not found. Please create a product with handle:', CONFIG.bundleProductHandle);
    }

    render();

    // Event listeners
    document.addEventListener('click', handleClick);

    // Listen for quick start events
    document.addEventListener('tlh:quickstart', (e) => {
      handleQuickStart(e.detail.preset);
    });

    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeProductModal();
      }
    });
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// Helper to get bundle item count (used by other scripts)
function getBundleItemCount() {
  if (window.TLHBundleBuilder) {
    const state = window.TLHBundleBuilder.getState();
    return state.totals.itemCount;
  }
  return 0;
}
